import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, CheckCircle2, TrendingUp, Zap, Shield } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useState } from "react";
import DemoBookingDialog from "@/components/DemoBookingDialog";
import { trackCTAClick } from "@/lib/analytics";

// Import customer logos
import americanCoin from "@/assets/clients/american-coin.png";
import firstFidelity from "@/assets/clients/first-fidelity.png";
import indexCigars from "@/assets/clients/index-cigars.png";
import lornaJane from "@/assets/clients/lorna-jane.png";
import ringersWestern from "@/assets/clients/ringers-western.png";
import universalCoin from "@/assets/clients/universal-coin.png";
import wittner from "@/assets/clients/wittner.svg";
import ommniLogo from "@/assets/ommni-logo.png";

export default function RetailTechStack2026() {
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  useSEO({
    title: "Prepare Your Retail Tech Stack for 2026 | Ommni",
    description: "Future-proof your commerce operations with a unified platform. Join leading retailers preparing their tech stack for 2026 with Ommni.",
    keywords: "retail technology 2026, commerce platform, tech stack modernization, retail operations",
  });

  const benefits = [
    {
      icon: Zap,
      title: "Real-Time Sync",
      description: "Product, pricing, and inventory unified across all channels"
    },
    {
      icon: TrendingUp,
      title: "Scale with Confidence",
      description: "Handle peak demand without system bottlenecks"
    },
    {
      icon: Shield,
      title: "Operational Truth",
      description: "One source of truth eliminates costly data conflicts"
    }
  ];

  const customerLogos = [
    { src: lornaJane, alt: "Lorna Jane" },
    { src: ringersWestern, alt: "Ringers Western" },
    { src: wittner, alt: "Wittner" },
    { src: americanCoin, alt: "American Coin" },
    { src: firstFidelity, alt: "First Fidelity" },
    { src: indexCigars, alt: "Index Cigars" },
    { src: universalCoin, alt: "Universal Coin" },
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Floating Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src={ommniLogo} alt="Ommni" className="h-8" />
          </div>
          <div className="flex gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                trackCTAClick("login", "retail_tech_stack_nav");
                window.location.href = "/auth";
              }}
            >
              Log In
            </Button>
            <Button 
              size="sm"
              onClick={() => {
                trackCTAClick("demo_nav", "retail_tech_stack_nav");
                setIsDemoDialogOpen(true);
              }}
            >
              Book Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-background via-primary/5 to-background pt-32 pb-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-6 rounded-full border bg-card px-4 py-2 text-sm font-medium">
                Limited Campaign • Ends January 2026
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Prepare Your Retail Tech Stack for{" "}
                <span className="text-primary">2026</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Stop fighting disconnected systems. Get Product, Pricing, and Inventory in sync across every channel—before peak season hits.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button 
                  variant="cta" 
                  size="lg"
                  onClick={() => setIsDemoDialogOpen(true)}
                >
                  Get Your Stack Assessment <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/free-trial">Start Free Trial</Link>
                </Button>
              </div>

              <DemoBookingDialog 
                open={isDemoDialogOpen} 
                onOpenChange={setIsDemoDialogOpen} 
              />

              {/* Social Proof */}
              <div className="text-sm text-muted-foreground mb-8">
                Trusted by leading retailers across 6 continents
              </div>

              {/* Customer Logos */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-8 items-center opacity-60 hover:opacity-100 transition-opacity">
                {customerLogos.map((logo, index) => (
                  <div key={index} className="flex items-center justify-center h-12">
                    <img 
                      src={logo.src} 
                      alt={logo.alt}
                      className="max-h-full max-w-full object-contain grayscale hover:grayscale-0 transition-all"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 border-b">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Why Retailers Are Moving to Ommni in 2026
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <Card key={index} className="p-8 text-center border-2 hover:border-primary/50 transition-colors">
                      <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* What You Get Section */}
        <section className="py-20 border-b bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                What's Included in Your 2026 Readiness Plan
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  "Real-time product, price & inventory sync",
                  "Multi-channel feed management",
                  "Automated workflow orchestration",
                  "API-first integration platform",
                  "Operational health dashboards",
                  "Peak-season stress testing",
                  "White-glove onboarding & migration",
                  "Dedicated success manager"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="relative overflow-hidden rounded-lg border-2 bg-card p-12 text-center">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/5 to-background"></div>
                <div className="relative z-10">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Lock in Your 2026 Advantage Today
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                    Get a personalized tech stack assessment and see how Ommni can eliminate your operational bottlenecks.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      variant="cta" 
                      size="lg"
                      onClick={() => setIsDemoDialogOpen(true)}
                    >
                      Schedule Assessment <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/pricing">View Pricing</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Minimal Footer */}
      <footer className="py-8 px-6 border-t border-border/50 text-center text-sm text-muted-foreground">
        <p>© 2025 Ommni. All rights reserved.</p>
        <div className="flex justify-center gap-4 mt-4">
          <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <span>•</span>
          <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
